import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Soma500mg() {
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const product = {
    id: 110,
    name: 'Soma 500mg',
    category: 'Pain Relief',
    images: [
      'https://public.readdy.ai/ai/img_res/a37042adb428c3d31370fcd873599d02.jpg',
      'https://readdy.ai/api/search-image?query=soma%20500mg%20white%20tablets%20blister%20pack%20detailed%20view%20professional%20pharmaceutical%20photography%20clean%20white%20background%20medical%20product%20shot%20muscle%20relaxant%20pills&width=600&height=600&seq=813&orientation=squarish'
    ],
    rating: 4.7,
    reviewCount: 634,
    inStock: true,
    description: 'Soma (Carisoprodol) 500mg is a higher-strength muscle relaxant used to treat acute musculoskeletal pain and discomfort. It works by blocking pain sensations between the nerves and the brain, providing effective relief from muscle spasms and injuries.',
    dosage: 'Take one tablet three times daily and at bedtime. Maximum treatment duration is 2-3 weeks. May be taken with or without food. Do not exceed recommended dosage.',
    manufacturer: 'Meda Pharmaceuticals',
    activeIngredient: 'Carisoprodol 500mg',
    prescriptionRequired: true,
    packages: [
      { pills: 60, price: 120, pricePerPill: 2.00 },
      { pills: 90, price: 165, pricePerPill: 1.83 },
      { pills: 120, price: 210, pricePerPill: 1.75 }
    ]
  };

  const handleAddToCart = () => {
    const cartItem = {
      id: product.id,
      name: product.name,
      package: product.packages[selectedPackage],
      quantity: quantity,
      image: product.images[0],
      totalPrice: product.packages[selectedPackage].price * quantity
    };

    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItemIndex = existingCart.findIndex(
      (item: any) => item.id === cartItem.id && item.package.pills === cartItem.package.pills
    );

    if (existingItemIndex > -1) {
      existingCart[existingItemIndex].quantity += quantity;
      existingCart[existingItemIndex].totalPrice = 
        existingCart[existingItemIndex].package.price * existingCart[existingItemIndex].quantity;
    } else {
      existingCart.push(cartItem);
    }

    localStorage.setItem('cart', JSON.stringify(existingCart));
    window.dispatchEvent(new Event('cartUpdated'));
    
    navigate('/cart');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pb-20">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center">
            <i className="ri-arrow-left-line text-xl text-gray-800"></i>
          </button>
          <h1 className="text-base font-semibold text-gray-800">Product Details</h1>
          <button onClick={() => navigate('/cart')} className="w-10 h-10 flex items-center justify-center">
            <i className="ri-shopping-cart-line text-xl text-gray-800"></i>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="pt-14">
        {/* Image Gallery */}
        <div className="relative bg-white">
          <div className="relative w-full h-80 overflow-hidden">
            <img
              src={product.images[currentImageIndex]}
              alt={product.name}
              className="w-full h-full object-contain"
            />
          </div>
          
          {/* Image Indicators */}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
            {product.images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentImageIndex === index ? 'bg-teal-500 w-6' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Stock Badge */}
          {product.inStock && (
            <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">
              In Stock
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="bg-white px-4 py-5 mt-2">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-1">{product.name}</h2>
              <p className="text-sm text-gray-500">{product.category}</p>
            </div>
          </div>

          {/* Rating */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <i
                  key={i}
                  className={`${
                    i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'
                  } text-yellow-400 text-sm`}
                ></i>
              ))}
            </div>
            <span className="text-sm font-medium text-gray-700">{product.rating}</span>
            <span className="text-sm text-gray-400">({product.reviewCount} reviews)</span>
          </div>

          {/* Description */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Description</h3>
            <p className="text-sm text-gray-600 leading-relaxed">{product.description}</p>
          </div>

          {/* Product Details */}
          <div className="space-y-3 mb-4">
            <div className="flex items-start gap-3">
              <i className="ri-capsule-line text-teal-500 text-lg mt-0.5"></i>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-0.5">Active Ingredient</p>
                <p className="text-sm font-medium text-gray-800">{product.activeIngredient}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <i className="ri-building-2-line text-teal-500 text-lg mt-0.5"></i>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-0.5">Manufacturer</p>
                <p className="text-sm font-medium text-gray-800">{product.manufacturer}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <i className="ri-file-list-3-line text-teal-500 text-lg mt-0.5"></i>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-0.5">Prescription</p>
                <p className="text-sm font-medium text-gray-800">
                  {product.prescriptionRequired ? 'Required' : 'Not Required'}
                </p>
              </div>
            </div>
          </div>

          {/* Dosage */}
          <div className="bg-blue-50 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-blue-500 text-lg mt-0.5"></i>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Dosage Instructions</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{product.dosage}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Package Selection */}
        <div className="bg-white px-4 py-5 mt-2">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Select Package</h3>
          <div className="space-y-3">
            {product.packages.map((pkg, index) => (
              <button
                key={index}
                onClick={() => setSelectedPackage(index)}
                className={`w-full p-4 rounded-xl border-2 transition-all ${
                  selectedPackage === index
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedPackage === index
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-gray-300'
                      }`}
                    >
                      {selectedPackage === index && (
                        <i className="ri-check-line text-white text-xs"></i>
                      )}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900">{pkg.pills} Pills</p>
                      <p className="text-xs text-gray-500">${pkg.pricePerPill.toFixed(2)} per pill</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-teal-600">${pkg.price}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Quantity Selector */}
        <div className="bg-white px-4 py-5 mt-2">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Quantity</h3>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center"
            >
              <i className="ri-subtract-line text-gray-700"></i>
            </button>
            <span className="text-lg font-semibold text-gray-900 min-w-[40px] text-center">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="w-10 h-10 rounded-full bg-teal-500 flex items-center justify-center"
            >
              <i className="ri-add-line text-white"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 shadow-lg">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Total Price</p>
            <p className="text-xl font-bold text-teal-600">
              ${(product.packages[selectedPackage].price * quantity).toFixed(2)}
            </p>
          </div>
          <button
            onClick={handleAddToCart}
            className="flex-1 bg-teal-500 text-white py-3 px-6 rounded-full font-semibold text-sm shadow-lg hover:bg-teal-600 transition-colors"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
}